<template>
  <v-app>
    <div>
      <mcity-toolbar
        :fullname="fullname"
        :username="username"
        :presentation-text="presentationText"
        :isUserAdmin="isUserAdmin"
        :title="title"
        :sidebarStartState="sidebarStartState"
        :width="350"
      >
        <template v-slot:buttons >
          <v-text-field
            dark
            v-model="searchString"
            label="Search"
            color="white"
            class="hidden-sm-and-down mr-2 search-margin"
            @keyup.enter="runSearch"
          />
          <v-dialog
            v-model="deletionDialog"
            width="500"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                text
                v-show="$route.meta.page === 'edit' && $route.params.pathMatch !== homeUri"
                class="hidden-sm-and-down"
                @click="deletionDialog = true"
              >Delete</v-btn>
            </template>
            <v-card>
              <v-card-title class="headline lighten-2">
                Make a note of why this is being deleted
              </v-card-title>
              <v-card-text>
                <v-textarea
                  v-model="deletionNote"
                  label="Note"
                />
                <v-switch
                  v-model="deleteChildren"
                  label="Delete child articles"
                ></v-switch>
                {{
                  deleteChildren
                  ? "Child articles will be deleted. Eg. if home/abc/123 exists, and abc is being deleted, then 123 will also be deleted."
                  : "Child articles will be moved to this article's parent. Eg. if home/abc/123 exists, and abc is being deleted, then 123 will be moved to home/123."
                }}
              </v-card-text>
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer/>
                <v-btn
                  text
                  @click="deletionDialog = false"
                >
                  Cancel
                </v-btn>
                <v-spacer/>
                <v-btn
                  color="red"
                  text
                  @click="handleDeleteClick"
                >
                  Delete
                </v-btn>
                <v-spacer/>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-btn
            text
            v-show="$route.meta.page === 'new' || $route.meta.page === 'history' || $route.meta.page === 'edit'"
            class="hidden-sm-and-down"
            @click="$router.push('/wiki/' + $route.params.pathMatch)"
          >Cancel</v-btn>
          <v-btn
            text
            v-show="$route.meta.page === 'article'"
            class="hidden-sm-and-down"
            @click="handlePrintClick"
          >Print</v-btn>
          <v-btn
            text
            v-show="isUserAdmin && showHistoryButton"
            :disabled="$route.meta.page === 'history' && !articleValid"
            class="hidden-sm-and-down"
            @click="handleHistoryClick"
          >{{ historyText }}</v-btn>
          <v-btn
            text
            v-show="isUserAdmin && showNewButton"
            :disabled="$route.meta.page === 'new' && !articleValid"
            class="hidden-sm-and-down"
            @click="handleNewClick"
          >{{ newText }}</v-btn>
          <v-btn
            text
            v-show="isUserAdmin && showEditButton"
            :disabled="$route.meta.page === 'edit' && !articleValid"
            class="hidden-sm-and-down"
            @click="handleEditClick"
          >{{ editText }}</v-btn>
        </template>

        <template v-slot:overflow-buttons>
          <ArticleHierarchy
            class="ml-0 mb-4 mt-1"
            @uriClick="(uri) => $router.push('/wiki/' + uri)"
            :selectedUri="$route.params.pathMatch"
            clickable
          />
          <v-select
            v-model="themeSelection"
            :items="themeOptions"
            label="Theme"
            class="mx-4"
            color="grey"
          />
        </template>
      </mcity-toolbar>
    </div>

    <v-main>
      <v-container fluid>
        <v-progress-circular v-show="isUserLoading" indeterminate class="mb-2"></v-progress-circular>
        <v-alert type="error" v-model="showRequestFailed" dismissible>{{ errorText }}</v-alert>
        <router-view/>
        <router-view name="auth"/>
        <auth-refresh v-if="getShowIframe"></auth-refresh>
      </v-container>
    </v-main>

    <v-footer dark color="primary" fixed app>
      <span>&copy; {{ copyright }} </span>
      <v-spacer></v-spacer>
      <span class="mr-1">{{ username }} : {{ today }}</span>
    </v-footer>
  </v-app>
</template>

<script>
import { mapMutations, mapGetters, mapActions, mapState } from 'vuex'
import authRefresh from '@mcity/mcity-vue-auth/src/components/RefreshAuthiFrame.vue'
import McityToolbar from '@mcity/mcity-vue-shared/components/McityToolbar.vue'
import { dom } from '@fortawesome/fontawesome-svg-core'
import ArticleHierarchy from './components/ArticleHierarchy.vue'
export default {
  name: 'App',
  components: {
    authRefresh,
    McityToolbar,
    ArticleHierarchy
  },
  methods: {
    ...mapMutations(['logError', 'setDarkModeVuex']),
    ...mapMutations('article', ['setDeletionExtras', 'copyArticleForEdit', 'setEditIsRestore']),
    ...mapActions('article/transactions', [
      'editArticleAction',
      'createArticleAction',
      'deleteArticleAction',
      'searchAction'
    ]),
    routeTo (path) {
      this.$router.push('/wiki/' + path)
    },
    handlePrintClick () {
      this.$htmlToPaper('print-content')
    },
    handleHistoryClick () {
      if (this.$route.meta.page === 'history') {
        this.saveArticle()
      } else {
        this.$router.push('/wiki/' + this.$route.params.pathMatch + '/history')
      }
    },
    handleEditClick () {
      if (this.$route.meta.page === 'edit') {
        this.saveArticle()
      } else {
        this.$router.push('/wiki/' + this.$route.params.pathMatch + '/edit')
      }
    },
    saveArticle () {
      const articlePath = this.$route.params.pathMatch
      const isRestore = this.$route.meta.page === 'history'
      this.setEditIsRestore(isRestore)
      if (isRestore) {
        this.copyArticleForEdit()
      }
      this.editArticleAction(articlePath)
    },
    handleDeleteClick () {
      const childrenAction = this.deleteChildren ? 'delete' : 'move'
      this.setDeletionExtras({ note: this.deletionNote, childrenAction: childrenAction })
      this.deleteArticleAction(this.$route.params.pathMatch)
      this.deletionDialog = false
      this.deletionNote = ''
      this.deleteChildren = false
      const newRoute = this.$route.params.pathMatch.split('/').slice(0, -1).join('/')
      this.$router.push(`/wiki/${newRoute.length > 0 ? newRoute : process.env.VUE_APP_HOME_URI}`)
    },
    handleNewClick () {
      // set currentArticle to articleEdits value here (?)
      if (this.$route.meta.page === 'new') this.createArticle()
      else {
        const path = this.$route.path
        if (path === '/') this.$router.push(`/wiki/${process.env.VUE_APP_HOME_URI}/new`)
        else this.$router.push(`${path}/new`)
      }
    },
    createArticle () {
      const uri = this.articleEdits.uri
      this.createArticleAction(uri)
    },
    runSearch () {
      this.searchAction()
      this.$router.push('/search')
    },
    setDarkMode (val) {
      let darkMode
      if (val === 'System') {
        darkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
          this.setDarkMode(e.matches ? 'Dark' : 'Light')
        })
      } else {
        darkMode = val === 'Dark'
      }
      document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light')
      this.$vuetify.theme.dark = darkMode
      this.setDarkModeVuex(darkMode)
    }
  },
  watch: {
    errors (val) {
      this.showRequestFailed = true
      const lastVal = val[val.length - 1]
      this.defaultText = 'Request Failed - If problem persists, please email mcity-engineering@umich.edu ' + lastVal
      this.errorText = (lastVal && 'data' in lastVal && lastVal.data) ? lastVal.data : this.defaultText
    },
    themeSelection (val) {
      this.setDarkMode(val)
      localStorage.setItem('theme', val)
    }
  },
  computed: {
    ...mapGetters(['getShowIframe']),
    ...mapState({
      getPresentMode: state => state.presentMode,
      isUserAdmin: state => state.isUserAdmin,
      isUserLoading: state => state.isUserLoading,
      errors: state => state.errors,
      user: state => state.session.user,
      getShowIframe: state => state.session.showIframe,
      articleEdits: state => state.article.articleEdits,
      searchString: state => state.article.searchString,
      articleHierarchy: state => state.article.articleHierarchy,
      articleValid: state => state.article.articleValid
    }),
    searchString: {
      get () {
        return this.$store.state.article.searchString
      },
      set (val) {
        this.$store.commit('article/setSearchString', val)
      }
    },
    sidebarStartState () {
      return document.documentElement.clientWidth >= 1250
    },
    presentationText () {
      return this.getPresentMode ? 'Private' : 'Presentation'
    },
    today () {
      const date = new Date()
      return date.toISOString().slice(0, 10)
    },
    username () {
      return this.user && this.user.username
        ? this.user.username
        : 'placeholder@email.com'
    },
    fullname () {
      const user = this.user
      return user && user.first_name && user.last_name
        ? `${user.first_name} ${user.last_name}`
        : 'Firstname Lastname'
    },
    showEditButton () {
      return this.$route.meta.page === 'edit' || this.$route.meta.page === 'article'
    },
    showNewButton () {
      return this.$route.meta.page === 'new' || this.$route.meta.page === 'article'
    },
    showHistoryButton () {
      return this.$route.meta.page === 'history' || this.$route.meta.page === 'article'
    },
    editText () {
      return this.$route.meta.page === 'edit' ? 'Save' : 'Edit'
    },
    newText () {
      return this.$route.meta.page === 'new' ? 'Create' : 'New'
    },
    historyText () {
      return this.$route.meta.page === 'history' ? 'Restore' : 'History'
    }
  },
  data () {
    return {
      homeUri: process.env.VUE_APP_HOME_URI,
      deletionNote: '',
      deleteChildren: false,
      deletionDialog: false,
      avatarMenu: false,
      clipped: false,
      showRequestFailed: false,
      errorText: null,
      iframeURL: '/refresh.html',
      title: 'Members',
      copyright: process.env.VUE_APP_COPYRIGHT || 'Copyright 2020 The Regents of the University of Michigan',
      colors: this.$vuetify.theme.currentTheme,
      themeSelection: false,
      themeOptions: ['Light', 'Dark', 'System']
    }
  },
  mounted () {
    dom.watch() // Required for FontAwesome SVG icons
    window.addEventListener('beforeprint', () => { this.handlePrintClick() })
    const localTheme = localStorage.getItem('theme')
    this.themeSelection = this.themeOptions.includes(localTheme) ? localTheme : 'System'
  }
}
</script>

<style lang="scss">
#app {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: left;
  color: #2c3e50;
  background-color: #f2f2f2;
}
#nav {
  padding: 30px;
  a {
    font-weight: bold;
    color: #2c3e50;
    &.router-link-exact-active {
      color: #42b983;
    }
  }
}
.small,
small {
  font-size: 80%;
  font-weight: 400;
}
.presentation {
  font-size: 120% !important;
}
.link {
  display: block;
}
.search-margin {
  margin-top: 12px !important;
}
// start dark mode css
:root {
  --text-color: #222222;
  --bg-color: white;
  --filter: invert(0);
}
[data-theme="dark"] {
  --text-color: #dddddd;
  --bg-color: #222222;
  --filter: invert(1) grayscale(1);
}
#app {
  background-color: var(--bg-color);
}
svg.invertible {
  filter: var(--filter);
}
*.invertible svg {
  filter: var(--filter);
}
span.subtitle-text {
  color: var(--text-color) !important;
}
div.v-window {
  color: var(--text-color);
}
span.v-icon.notranslate.theme--dark.primary--text {
  color: var(--text-color) !important;
}
div > div.tui-editor-contents * {
  color: var(--text-color);
}
div.tui-editor-contents > table > thead > tr > th {
  color: white;
}
div.tui-editor-contents pre {
  background-color: var(--bg-color);
}
span.hierarchy-entry.clickable {
  color: var(--text-color)
}
div.v-list-item__title > a {
  color: var(--text-color)
}
div.v-menu__content.theme--dark.v-menu__content--fixed.menuable__content__active div.v-list-item__title {
  color: var(--text-color);
}
td.history-date-text {
  color: var(--text-color);
}
a {
  color: #296eef !important;
}
div.hierarchy-card.v-card.v-sheet.theme--dark.rounded-0 {
  background-color: var(--bg-color) !important;
  color: var(--text-color) !important;
}
div.te-ww-container {
  background-color: var(--bg-color) !important;
}
// end dark mode css
</style>
